<template>
  <div class="container-fluid py-4 gray-bg">
    
    <!-- Otsikko ja Päivitysnappi -->
    <div class="d-flex justify-content-between align-items-center mb-4 bg-white p-3 shadow-sm rounded">
      <h4 class="mb-0 font-weight-bold text-dark">
        <i class="fas fa-robot mr-2 text-primary"></i>AI-kustannusanalyysi
      </h4>
      <div class="d-flex align-items-center">
        <div class="btn-group mr-2" role="group">
          <button
            v-for="d in [7, 30, 90]"
            :key="d"
            @click="setDays(d)"
            class="btn btn-sm rounded-pill mr-1"
            :class="days === d ? 'btn-primary' : 'btn-outline-primary'"
          >{{ d }} pv</button>
        </div>
        <button @click="fetchData" class="btn btn-outline-primary btn-sm rounded-pill" :disabled="loading">
          <i class="fas fa-sync-alt mr-1" :class="{'fa-spin': loading}"></i> 
          {{ loading ? 'Ladataan...' : 'Päivitä' }}
        </button>
      </div>
    </div>

    <!-- Latausilmoitus -->
    <div v-if="loading && !stats" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">Haetaan AI-metriikoita...</p>
    </div>

    <!-- Pääsisältö (Näytetään kun data on ladattu) -->
    <div v-if="stats">

      <!-- Arvio-varoitus jos ei viela toteutunutta billing-dataa -->
      <div v-if="stats.summary.cost_source === 'estimate'" class="alert alert-warning py-2 px-3 mb-3">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        Kustannus on karkea arvio - toteutunutta laskutusdataa ei ole vielä saatavilla.
      </div>

      <!-- Ylärivin kortit (KPI) -->
      <div class="row mb-4">
        <!-- Kokonaiskustannus -->
        <div class="col-md-3">
          <div class="card border-left-green shadow-sm h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Kustannus ({{ stats.summary.currency }})
                  </div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                    {{ formatCurrency(stats.summary.total_cost, stats.summary.currency) }}
                  </div>
                  <span class="badge" :class="stats.summary.cost_source === 'billing_export' ? 'badge-success' : 'badge-warning'">
                    {{ stats.summary.cost_source === 'billing_export' ? 'Toteutunut' : 'Arvio' }}
                  </span>
                </div>
                <div class="col-auto">
                  <i class="fas fa-euro-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pyynnöt -->
        <div class="col-md-3">
          <div class="card border-left-blue shadow-sm h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">Pyynnöt yhteensä</div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">{{ formatNumber(stats.summary.total_requests) }} kpl</div>
                </div>
                <div class="col-auto">
                  <i class="fas fa-cloud-upload-alt fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tokenit -->
        <div class="col-md-3">
          <div class="card border-left-info shadow-sm h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-info text-uppercase mb-1">Tokenit yhteensä</div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">{{ formatNumber(stats.summary.total_tokens) }}</div>
                </div>
                <div class="col-auto">
                  <i class="fas fa-coins fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Keskiviive -->
        <div class="col-md-3">
          <div class="card border-left-warning shadow-sm h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-warning text-uppercase mb-1">Keskiviive (Avg)</div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">{{ stats.summary.avg_latency.toFixed(2) }} s</div>
                </div>
                <div class="col-auto">
                  <i class="fas fa-stopwatch fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Paivakohtainen kustannus -->
      <div class="card shadow-sm mb-4" v-if="dailyChartData.length">
        <div class="card-header py-3 bg-indigo text-white">
          <h6 class="m-0 font-weight-bold">Kustannus päivittäin ({{ days }} pv)</h6>
        </div>
        <div class="card-body">
          <div style="height: 300px;">
            <BarChartBasic :key="days" :data="dailyChartData" :label="'Kustannus (' + stats.summary.currency + ')'" height="300" />
          </div>
        </div>
      </div>

      <!-- Taulukko (Mallikohtainen vertailu) -->
      <div class="card shadow-sm mb-4">
        <div class="card-header py-3 bg-indigo text-white">
          <h6 class="m-0 font-weight-bold">Mallikohtainen vertailu ({{ days }} pv)</h6>
        </div>
        <div class="card-body p-0 text-dark">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="bg-light">
                <tr>
                  <th class="border-top-0">Malli / SKU</th>
                  <th class="border-top-0 text-right">Kustannus</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(model, index) in stats.models" :key="index">
                  <td class="align-middle">
                    <span class="font-weight-bold text-dark">{{ model.model_name }}</span>
                  </td>
                  <td class="align-middle text-right text-primary font-weight-bold">
                    {{ formatCurrency(model.cost, model.currency) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import BarChartBasic from '@/components/BarChartBasic';

export default {
  components: { BarChartBasic },
  data() {
    return {
      loading: false,
      stats: null,
      days: 30,
    };
  },
  computed: {
    dailyChartData() {
      if (!this.stats || !this.stats.daily) return [];
      return this.stats.daily.map(d => ({ eventday: d.date, eventcount: d.cost }));
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    setDays(d) {
      this.days = d;
      this.fetchData();
    },
    fetchData() {
      this.loading = true;
      axios.get('/1.0/front/ai/metrics', { params: { days: this.days } })
        .then(response => {
          this.stats = response.data;
        })
        .catch(error => {
          console.error("Virhe ladattaessa AI-tilastoja:", error);
          // Tähän voisi lisätä toast-ilmoituksen (esim. this.$toast.error(...))
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatCurrency(value, currency) {
      if (!value) return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: currency || 'EUR' }).format(0);
      // Käytetään vähintään 2 ja enintään 4 desimaalia, koska AI-kustannukset ovat pieniä
      return new Intl.NumberFormat('fi-FI', { 
        style: 'currency', 
        currency: currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4 
      }).format(value);
    },
    formatNumber(value) {
      if (!value) return '0';
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    }
  }
};
</script>

<style scoped>
.gray-bg { background-color: #f8f9fc; min-height: 100vh; }
.bg-indigo { background-color: #4e73df; }
.text-xs { font-size: .7rem; }

/* Reunakorostukset kortteihin */
.border-left-green { border-left: .25rem solid #1cc88a !important; }
.border-left-blue { border-left: .25rem solid #4e73df !important; }
.border-left-info { border-left: .25rem solid #36b9cc !important; }
.border-left-warning { border-left: .25rem solid #f6c23e !important; }

/* Taulukon hienosäätö vastaamaan kuvaasi */
.table thead th {
  font-size: 0.8rem;
  text-transform: none;
  color: #858796;
  font-weight: 600;
  border-bottom: none;
}
.table td {
  border-top: 1px solid #e3e6f0;
  vertical-align: middle;
}
.progress {
  border-radius: 10px;
}
</style>